import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

/**
 * What an order costs, as a stack of labelled amounts:
 *
 *   <OrderTotals>
 *     <OrderTotalsLine variant={OrderTotalsVariant.LEAD}>
 *       <OrderTotalsLabel variant={OrderTotalsVariant.LEAD}>Celková cena s DPH:</OrderTotalsLabel>
 *       <OrderTotalsAmount variant={OrderTotalsVariant.LEAD}>3 798 Kč</OrderTotalsAmount>
 *     </OrderTotalsLine>
 *     <OrderTotalsLine variant={OrderTotalsVariant.MUTED}>…</OrderTotalsLine>
 *   </OrderTotals>
 *
 * How many lines there are, and in what order, is tenant JSX — a cart shows the
 * price with VAT over the price without it, a checkout summary adds shipping and
 * payment above them, and neither is a prop. What each line paints is the
 * appearance axis: one line leads and the rest sit under it.
 *
 * Nothing here formats money or knows a currency. Both the label and the amount
 * arrive as children, already in the tenant's words and its locale's digits.
 */

/** The stack. The gap is between lines, so it belongs to what holds them. */
const orderTotalsClasses = 'flex flex-col gap-1';

export type OrderTotalsProps = ComponentProps<'div'>;

/** The order's amounts, stacked. */
export const OrderTotals = ({ className, ...props }: OrderTotalsProps) => (
  <div data-slot="order-totals" className={clsxm(orderTotalsClasses, className)} {...props} />
);

/**
 * One labelled amount.
 *
 * `items-end` rather than a baseline: the two sides run at different type sizes
 * and their descenders are what a shopper's eye lines up on.
 */
export const orderTotalsLineVariants = cva('flex', {
  variants: {
    variant: {
      /** The amount the order actually comes to. */
      lead: 'items-end gap-2',
      /** Everything the lead line is qualified by, quieter than it. */
      muted: 'text-foreground-muted items-end gap-2',
      /** The order's total inside a panel: the label at one edge, the amount at the other. */
      panelLead: 'items-center justify-between tracking-tight',
      /** A qualifying amount in a panel, spread the same way and quieter. */
      panelMuted: 'items-center justify-between',
      /**
       * The order's total at the foot of a checkout summary card: the label at
       * one edge and the amount at the other, with what qualifies the label sat
       * under it rather than beside it.
       */
      cardLead: 'items-center justify-between',
      /** The opt-out: a tenant painting the line itself. */
      unstyled: '',
    },
  },
});

type OrderTotalsVariantName = NonNullable<VariantProps<typeof orderTotalsLineVariants>['variant']>;

export type OrderTotalsVariant = OrderTotalsVariantName;

/**
 * The section's appearance axis, shared by the line and both halves of it.
 * Derived from the line recipe and bound to it by `satisfies`, so the union and
 * the recipes cannot drift.
 */
export const OrderTotalsVariant = {
  /** The amount the order actually comes to. */
  LEAD: 'lead',
  /** A qualifying amount under it. */
  MUTED: 'muted',
  /**
   * The same amount in a panel narrow enough that label and amount sit at
   * opposite edges rather than beside each other — where the label also carries
   * what it is qualified by, under itself.
   */
  PANEL_LEAD: 'panelLead',
  /** A qualifying amount in a panel. */
  PANEL_MUTED: 'panelMuted',
  /** The order's total at the foot of a checkout summary card. */
  CARD_LEAD: 'cardLead',
  /** The opt-out: a tenant painting the part itself. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, OrderTotalsVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type OrderTotalsVariantProps = { readonly variant: OrderTotalsVariant };

export type OrderTotalsLineProps = ComponentProps<'div'> & OrderTotalsVariantProps;

/** One label beside one amount. */
export const OrderTotalsLine = ({ className, variant, ...props }: OrderTotalsLineProps) => (
  <div
    data-slot="order-totals-line"
    className={clsxm(orderTotalsLineVariants({ variant }), className)}
    {...props}
  />
);

/**
 * What the amount beside it is.
 *
 * `whitespace-nowrap` on the lead label: it is the longest string in the block
 * and the block sits at the trailing edge of a column, so a wrap there moves the
 * amount rather than the label.
 */
export const orderTotalsLabelVariants = cva('', {
  variants: {
    variant: {
      lead: 'text-foreground text-xl/7 whitespace-nowrap',
      muted: 'text-base/6',
      panelLead: 'font-heading text-lg/7',
      panelMuted: 'text-foreground-muted tracking-tight',
      cardLead: 'font-heading text-foreground text-xl/7 tracking-tight',
      unstyled: '',
    },
  },
});

export type OrderTotalsLabelProps = ComponentProps<'span'> & OrderTotalsVariantProps;

/** What the amount beside it is. */
export const OrderTotalsLabel = ({ className, variant, ...props }: OrderTotalsLabelProps) => (
  <span
    data-slot="order-totals-label"
    className={clsxm(orderTotalsLabelVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The label over what qualifies it.
 *
 * Its own part rather than the label growing a second child: the qualifier is a
 * separate sentence with its own type scale, and a tenant that has none composes
 * the label alone.
 */
const orderTotalsLabelGroupClasses = 'flex flex-col';

export type OrderTotalsLabelGroupProps = ComponentProps<'div'>;

/** A label with what qualifies it under it. */
export const OrderTotalsLabelGroup = ({ className, ...props }: OrderTotalsLabelGroupProps) => (
  <div
    data-slot="order-totals-label-group"
    className={clsxm(orderTotalsLabelGroupClasses, className)}
    {...props}
  />
);

/**
 * What qualifies the label above it — that the amount includes tax, that it is
 * an estimate. Quieter and smaller than the label by definition, so it carries
 * no appearance axis.
 */
const orderTotalsNoteClasses = 'text-foreground-muted text-xs/4';

export type OrderTotalsNoteProps = ComponentProps<'span'>;

/** The qualifier under a label. */
export const OrderTotalsNote = ({ className, ...props }: OrderTotalsNoteProps) => (
  <span
    data-slot="order-totals-note"
    className={clsxm(orderTotalsNoteClasses, className)}
    {...props}
  />
);

/** The amount itself, already formatted by whoever knows the currency. */
export const orderTotalsAmountVariants = cva('', {
  variants: {
    variant: {
      lead: 'lap:text-3xl/8 text-2xl font-bold',
      muted: 'lap:text-xl/7 text-lg',
      panelLead: 'text-[28px]/9 font-medium',
      panelMuted: 'tracking-tight',
      cardLead: 'text-foreground text-[28px]/9 font-semibold',
      unstyled: '',
    },
  },
});

export type OrderTotalsAmountProps = ComponentProps<'span'> & OrderTotalsVariantProps;

/** The amount. */
export const OrderTotalsAmount = ({ className, variant, ...props }: OrderTotalsAmountProps) => (
  <span
    data-slot="order-totals-amount"
    className={clsxm(orderTotalsAmountVariants({ variant }), className)}
    {...props}
  />
);
